import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  Wrench, Search, Plus, Trash2, Upload, RefreshCw, Loader2,
  CheckCircle2, XCircle,
} from "lucide-react";

export default function AdminToolCatalog() {
  const { isPrivileged } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [newRow, setNewRow] = useState({ Tool_Name: "", Cat_ID: "", Category: "General", Notes: "" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkReplace, setBulkReplace] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/comed-tools", { params: { active_only: activeOnly, q: q.trim() || undefined } });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isPrivileged) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivileged, activeOnly]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  if (!isPrivileged) return <Navigate to="/" replace />;

  const onCreate = async () => {
    if (!newRow.Tool_Name.trim() || !newRow.Cat_ID.trim()) {
      toast.error("Tool name and Cat ID are required");
      return;
    }
    try {
      await api.post("/comed-tools", newRow);
      toast.success("Added");
      setNewRow({ Tool_Name: "", Cat_ID: "", Category: "General", Notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this tool from the catalog?")) return;
    try {
      await api.delete(`/comed-tools/${id}`);
      toast.success("Removed");
      setRows((s) => s.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleActive = async (row) => {
    try {
      const { data } = await api.patch(`/comed-tools/${row.id}`, { Active: !row.Active });
      setRows((s) => s.map((r) => (r.id === row.id ? data : r)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const bulkImport = async () => {
    if (!bulkText.trim()) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/comed-tools/bulk-import", {
        text: bulkText,
        replace_existing: bulkReplace,
      });
      toast.success(`Imported ${data.inserted} (${data.skipped} duplicates skipped)`);
      if (data.errors?.length) {
        toast.warning(`${data.errors.length} lines couldn't be parsed`);
      }
      setBulkText("");
      setBulkReplace(false);
      setBulkOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 py-5 max-w-5xl mx-auto" data-testid="admin-tool-catalog">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 mb-1 flex items-center gap-1.5">
            <Wrench className="h-3 w-3" /> Admin
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-slate-900 uppercase tracking-tight leading-none">
            ComEd Tool Catalog
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Master list of ComEd-approved tools with their Cat IDs. Crews pick from this list on the Tool Request screen.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setBulkOpen((v) => !v)}
            data-testid="tool-catalog-bulk-toggle"
            className="rg-tap bg-slate-900 hover:bg-slate-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center gap-2 px-4 min-h-[44px] text-xs border-2 border-slate-700"
          >
            <Upload className="h-4 w-4" /> Bulk import
          </button>
          <button
            type="button"
            onClick={load}
            data-testid="tool-catalog-refresh"
            className="rg-tap bg-white border-2 border-slate-300 hover:border-slate-900 rounded-sm flex items-center gap-2 px-4 min-h-[44px] text-xs font-bold uppercase tracking-wider text-slate-700"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {bulkOpen && (
        <div
          className="rg-card p-4 mb-4 border-2 border-emerald-300 bg-emerald-50"
          data-testid="tool-catalog-bulk-panel"
        >
          <div className="text-xs font-bold uppercase tracking-wider text-emerald-800 mb-1">
            Paste your ComEd tool list
          </div>
          <div className="text-[11px] text-slate-700 mb-2">
            One per line: <code className="bg-white px-1 border rounded-sm">Tool Name | Cat ID | Category</code> (category optional). Empty lines and lines starting with <code className="bg-white px-1 border rounded-sm">#</code> are ignored.
          </div>
          <textarea
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            rows={8}
            placeholder={"Megger MIT415/2 5kV Insulation Tester | 1000-12345 | Insulation Tester\nAEMC 5060 Insulation Tester | 1000-67890 | Insulation Tester"}
            data-testid="tool-catalog-bulk-textarea"
            className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-sm bg-white focus:border-emerald-700 outline-none font-mono"
          />
          <label className="flex items-center gap-2 mt-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={bulkReplace}
              onChange={(e) => setBulkReplace(e.target.checked)}
              data-testid="tool-catalog-bulk-replace"
              className="accent-rose-700"
            />
            <span className="text-rose-800 font-bold">
              Replace the entire catalog (deletes existing tools first)
            </span>
          </label>
          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={() => { setBulkText(""); setBulkOpen(false); }}
              className="rg-tap px-3 border-2 border-slate-300 rounded-sm text-xs font-bold uppercase tracking-wider text-slate-700 bg-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={bulkImport}
              disabled={!bulkText.trim() || bulkBusy}
              data-testid="tool-catalog-bulk-submit"
              className="rg-tap bg-emerald-700 hover:bg-emerald-800 text-white px-4 rounded-sm text-xs font-bold uppercase tracking-wider disabled:opacity-50 border-2 border-emerald-900 flex items-center gap-1.5"
            >
              {bulkBusy && <Loader2 className="h-3 w-3 animate-spin" />}
              Import
            </button>
          </div>
        </div>
      )}

      {/* Add-one row */}
      <div className="rg-card p-3 mb-3 grid grid-cols-1 md:grid-cols-5 gap-2">
        <input
          value={newRow.Tool_Name}
          onChange={(e) => setNewRow({ ...newRow, Tool_Name: e.target.value })}
          placeholder="Tool name"
          data-testid="tool-catalog-new-name"
          className="rg-tap border-2 border-slate-300 rounded-sm px-3 text-sm bg-white focus:border-emerald-700 outline-none md:col-span-2"
        />
        <input
          value={newRow.Cat_ID}
          onChange={(e) => setNewRow({ ...newRow, Cat_ID: e.target.value })}
          placeholder="Cat ID"
          data-testid="tool-catalog-new-catid"
          className="rg-tap border-2 border-slate-300 rounded-sm px-3 text-sm bg-white font-mono focus:border-emerald-700 outline-none"
        />
        <input
          value={newRow.Category}
          onChange={(e) => setNewRow({ ...newRow, Category: e.target.value })}
          placeholder="Category"
          data-testid="tool-catalog-new-category"
          className="rg-tap border-2 border-slate-300 rounded-sm px-3 text-sm bg-white focus:border-emerald-700 outline-none"
        />
        <button
          type="button"
          onClick={onCreate}
          data-testid="tool-catalog-new-submit"
          className="rg-tap bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 border-2 border-emerald-900"
        >
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>

      <div className="rg-card p-3 mb-3 flex items-center gap-2">
        <Search className="h-4 w-4 text-slate-500 flex-shrink-0" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="tool-catalog-search"
          placeholder="Search…"
          className="flex-1 outline-none text-sm"
        />
        <label className="flex items-center gap-1 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
            data-testid="tool-catalog-active-only"
            className="accent-emerald-700"
          />
          <span className="font-bold uppercase tracking-wider text-slate-700">Active only</span>
        </label>
      </div>

      {loading ? (
        <div className="rg-card p-12 text-center text-slate-500 flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="rg-card p-12 text-center text-slate-500" data-testid="tool-catalog-empty">
          <Wrench className="h-10 w-10 mx-auto text-slate-300 mb-2" />
          <div className="font-bold text-slate-700">No tools yet.</div>
          <div className="text-xs mt-1">Use <strong>Bulk import</strong> above to paste your canonical list.</div>
        </div>
      ) : (
        <div className="rg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-600">
              <tr>
                <th className="py-2 px-3 text-left">Tool</th>
                <th className="py-2 px-3 text-left w-32">Cat ID</th>
                <th className="py-2 px-3 text-left w-40">Category</th>
                <th className="py-2 px-3 text-left w-24">Active</th>
                <th className="py-2 px-3 text-right w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`tool-catalog-row-${r.Cat_ID}`}>
                  <td className="py-2 px-3 font-bold text-slate-900">{r.Tool_Name}</td>
                  <td className="py-2 px-3 font-mono">{r.Cat_ID}</td>
                  <td className="py-2 px-3 text-slate-700">{r.Category || "General"}</td>
                  <td className="py-2 px-3">
                    <button
                      type="button"
                      onClick={() => toggleActive(r)}
                      data-testid={`tool-catalog-active-${r.Cat_ID}`}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border-2 ${
                        r.Active
                          ? "bg-emerald-700 text-white border-emerald-900"
                          : "bg-slate-200 text-slate-600 border-slate-400"
                      }`}
                    >
                      {r.Active ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                      {r.Active ? "Active" : "Off"}
                    </button>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button
                      type="button"
                      onClick={() => onDelete(r.id)}
                      data-testid={`tool-catalog-delete-${r.Cat_ID}`}
                      className="p-1.5 hover:bg-rose-50 rounded-sm text-rose-600 hover:text-rose-800"
                      title="Remove"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
